import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ContactRow } from "@hub/shared";
import { Avatar, Button, Input, Modal, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { ContactRowView } from "@/features/people/ContactRowView";

/**
 * Merge one or more people into a target. With a single source the user searches for the other person; with several
 * preselected the user only picks which record survives.
 */
export function MergeModal({ open, onClose, contactIds }: { open: boolean; onClose: () => void; contactIds: string[] }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState<string | null>(contactIds[0] ?? null);
  const [extra, setExtra] = useState<ContactRow | null>(null);
  const rows = useEngineQuery("getContactRows", { contactIds }, { enabled: open });
  const search = useEngineQuery("queryContacts", { query, limit: 8 }, { enabled: open && contactIds.length === 1 && query.trim().length > 0 });
  const merge = useEngineMutation("mergeContacts");
  const unmerge = useEngineMutation("unmergeContact");
  const all = [...(rows.data ?? []), ...(extra ? [extra] : [])];
  const canMerge = all.length >= 2 && target;

  const submit = async () => {
    if (!target) return;
    const sources = all.map((r) => r.contactId).filter((id) => id !== target);
    const res = await merge.mutateAsync({ targetContactId: target, sourceContactIds: sources });
    toast(`Merged ${sources.length + 1} records`, "success", {
      label: "Undo",
      onClick: () => Promise.all(res.mergeIds.map((mergeId) => unmerge.mutateAsync({ mergeId }))).then(() => toast("Merge undone", "success")).catch(() => undefined),
    });
    onClose();
    navigate(`/contact/${target}`);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Merge people"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!canMerge} loading={merge.isPending} onClick={submit}>Merge into selected</Button>
        </>
      }
    >
      <div className="u-col" style={{ gap: 12 }}>
        {contactIds.length === 1 && !extra && (
          <div className="u-col">
            <Input autoFocus placeholder="Find the other record by name" value={query} onChange={(e) => setQuery(e.target.value)} />
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              {(search.data?.items ?? []).filter((r) => r.contactId !== contactIds[0]).map((r) => (
                <ContactRowView key={r.contactId} row={r} showCheckbox={false} onOpen={() => { setExtra(r); setQuery(""); }} />
              ))}
            </div>
          </div>
        )}
        {all.length >= 2 && (
          <>
            <div className="u-small u-muted">Pick the record to keep. Everything from the others moves into it: sources, notes, reminders, groups and interactions. Merges can be undone from the person page.</div>
            {all.map((r) => (
              <label key={r.contactId} className="u-row" style={{ gap: 10, padding: 8, border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", background: target === r.contactId ? "var(--bg-selected)" : undefined }}>
                <input type="radio" name="merge-target" checked={target === r.contactId} onChange={() => setTarget(r.contactId)} />
                <Avatar name={r.displayName} src={r.avatarUrl} size={32} tier={r.bucket} seed={r.contactId} />
                <div className="u-grow">
                  <div style={{ fontWeight: 600 }}>{r.displayName}</div>
                  <div className="u-small u-muted">{[r.headline, r.primaryEmail, r.location].filter(Boolean).join(" / ")}</div>
                </div>
                {extra?.contactId === r.contactId && <Button size="sm" variant="ghost" onClick={(e) => { e.preventDefault(); setExtra(null); }}>Remove</Button>}
              </label>
            ))}
          </>
        )}
      </div>
    </Modal>
  );
}
